import React from 'react';
import styled, { keyframes } from 'styled-components';

/* ---------------------------
   Styled Components
--------------------------- */
const Section = styled.section`
  padding: 5rem 0;
  background: #ffffff;

  @media (max-width: 768px) {
    padding: 3rem 0;
  }
`;

const HeaderWrapper = styled.div`
  text-align: center;
  padding: 0 5%;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const SectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(55, 48, 163, 0.05);
  border: 1px solid rgba(55, 48, 163, 0.1);
  padding: 6px 16px;
  border-radius: 4px;
  margin-bottom: 1.2rem;
  font-family: 'Times New Roman', serif;
  font-size: 0.78rem;
  font-weight: 700;
  color: #3730A3;
  letter-spacing: 1.5px;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: #3730A3;
    border-radius: 50%;
  }
`;

const SectionTitle = styled.h2`
  font-family: 'Times New Roman', serif;
  font-size: 2.5rem;
  color: #1a1a2e;
  font-weight: 800;
  margin-bottom: 0.8rem;

  span {
    color: #3730A3;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 2rem;
  }

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled.p`
  font-family: 'Times New Roman', serif;
  font-size: 1.1rem;
  color: #4a4a6a;
  max-width: 550px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.95rem;
  }
`;

/* ---- INDIGO STRIP ---- */
const StripContainer = styled.div`
  background: linear-gradient(160deg, #0b0b77 0%, #12104d 40%, #0a0a3a 70%, #0b0b77 100%);
  padding: 4rem 5%;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 2.5rem 5%;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(rgba(255,255,255,0.012) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.012) 1px, transparent 1px);
    background-size: 40px 40px;
    mask-image: radial-gradient(ellipse at 50% 50%, black 30%, transparent 70%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: -80px;
    right: -80px;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(167, 139, 250, 0.06) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
`;

const StripInner = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const StripTitle = styled.h3`
  font-family: 'Times New Roman', serif;
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
  text-align: center;
  margin-bottom: 3rem;
  letter-spacing: 1px;

  strong {
    color: #ffffff;
    font-weight: 800;
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
`;

const LogoRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 2.5rem;
  align-items: center;
  justify-items: center;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 3rem;
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2.5rem;
  }
`;

const LogoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;
  padding: 0.5rem;

  &:hover {
    transform: translateY(-4px);
  }
`;

const LogoImg = styled.img`
  height: ${p => p.$height || '75px'};
  width: auto;
  max-width: 180px;
  object-fit: contain;
  opacity: 1;
  filter: brightness(1.2) contrast(1.1);
  transition: all 0.3s ease;

  ${LogoItem}:hover & {
    opacity: 1;
    transform: scale(1.05);
    filter: brightness(1.4) contrast(1.15);
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    height: ${p => p.$laptopHeight || '65px'};
    max-width: 160px;
  }

  @media (max-width: 768px) {
    height: ${p => p.$mobileHeight || '55px'};
    max-width: 130px;
  }
`;

const LogoName = styled.span`
  font-family: 'Times New Roman', serif;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
  letter-spacing: 0.5px;
  text-align: center;
  transition: color 0.3s ease;

  ${LogoItem}:hover & {
    color: #c4b5fd;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.75rem;
  }
`;

/* ---------------------------
   Component
--------------------------- */
const CertificationsSection = () => {
  const certifications = [
    {
      logo: "/images/certifications/ahima.png",
      name: "AHIMA",
      height: "200px",
      laptopHeight: "150px",
      mobileHeight: "55px"
    },
    {
      logo: "/images/certifications/aapc.png",
      name: "AAPC",
      height: "200px",
      laptopHeight: "150px",
      mobileHeight: "38px"
    },
    {
      logo: "/images/certifications/ccs.jpg",
      name: "CCS Certification",
      height: "200px",
      laptopHeight: "150px",
      mobileHeight: "42px"
    },
    {
      logo: "/images/certifications/cdip.png",
      name: "CDIP Certification",
      height: "200px",
      laptopHeight: "150px",
      mobileHeight: "42px"
    },
    {
      logo: "/images/certifications/cpc.png",
      name: "CPC Certification",
      height: "200px",
      laptopHeight: "150px",
      mobileHeight: "38px"
    },
    {
      logo: "/images/certifications/icd10.webp",
      name: "ICD-10 CM/PCS",
      height: "200px",
      laptopHeight: "150px",
      mobileHeight: "38px"
    }
  ];

  return (
    <Section>
      <HeaderWrapper>
        <SectionTitle>
          Recognized <span>Certifications</span>
        </SectionTitle>
        <SectionSubtitle>
          Our training programs prepare you for globally recognized healthcare certifications
        </SectionSubtitle>
      </HeaderWrapper>

      <StripContainer>
        <StripInner>
          <StripTitle>
            Our Students Get <strong>Certified By</strong>
          </StripTitle>
          <LogoRow>
            {certifications.map((cert, index) => (
              <LogoItem key={index}>
                <LogoImg 
                  src={cert.logo} 
                  alt={cert.name}
                  $height={cert.height}
                  $laptopHeight={cert.laptopHeight}
                  $mobileHeight={cert.mobileHeight}
                />
                <LogoName className="cert-name">{cert.name}</LogoName>
              </LogoItem>
            ))}
          </LogoRow>
        </StripInner>
      </StripContainer>
    </Section>
  );
};

export default CertificationsSection;